import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { MatButtonModule } from '@angular/material/button';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { NgIf, NgFor } from '@angular/common';

import { pageTransitions } from '../../../../shared/animations/page-transitions';
import {
	StepClickedEvent,
	StepStatus,
	StepperComponent
} from '../../../../shared/components/stepper/stepper.component';
import { UNIT_OPTIONS } from '../../../../shared/models/list';
import { User } from '../../../../shared/models/user';
import { LegalDocumentService } from '../../../../shared/services/legal-document.service';
import { LocationService } from '../../../../shared/services/location.service';
import { TranslationService } from '../../../../shared/services/translation.service';
import { UserService } from '../../../../shared/services/user.service';
import { HYPHEN } from '../../../../shared/utils/constant';
import { AuctionOffer, Bid, Bundle } from '../../../models/auction-offer';
import { staticTransportModeValues } from '../../../models/mills';
import { AUCTION_SERVICE, AuctionOfferService } from '../../../services/auction-offer.service';
import { GlobalErrorHandler } from '../../../../shared/utils/error-handler';
import { SnackbarInternalNotAuthorizedComponent } from '../../utils/snackbar-internal-not-authorized.component';
import { NumberPipe } from '../../../../shared/pipes/number.pipe';
import { DatePipe } from '../../../../shared/pipes/date.pipe';
import { LoadingSpinnerComponent } from '../../../../shared/components/utils/loading-spinner.component';
import {
	LegalDocumentAgreementComponent
} from '../../../../shared/components/legal/legal-document-user-agreement/legal-document-agreement.component';
import { PageHeaderComponent } from '../../../../shared/components/page-header/page-header.component';

@Component({
	selector: 'copo-auction-offer-confirmation-screen',
	templateUrl: './auction-offer-confirmation-screen.component.html',
	styleUrl: './auction-offer-confirmation-screen.component.css',
	animations: pageTransitions,
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		NgIf,
		CdkScrollable,
		PageHeaderComponent,

		MatButtonModule,
		TranslateModule,
		LegalDocumentAgreementComponent,
		StepperComponent,
		MatIconModule,
		NgFor,
		LoadingSpinnerComponent,
		DatePipe,
		NumberPipe
	]
})
export class AuctionOfferConfirmationScreenComponent implements OnInit, OnDestroy {
	isLoading = true;
	isConfirmDisabled = true;
	isUserAgreementsLoading = true;
	submitting = false;
	auctionOffer!: AuctionOffer;
	user: User;
	userSoldTos!: string[];
	usersoldToSub: Subscription;
	defaultSoldTo: string;
	soldToName!: string;
	bidsMap = new Map<number, Bid>();
	notAgreedLegalDocumentIds: number[] = [];
	auctionServiceCode = AUCTION_SERVICE;
	auctionLabelValue: string;

	readonly UNDERSCORE = HYPHEN;
	readonly unitBatchOptions = UNIT_OPTIONS;

	readonly steps;

	private auctionIdSub?: Subscription;
	constructor(
		private readonly auctionOfferService: AuctionOfferService,
		private readonly translationService: TranslationService,
		private readonly locationService: LocationService,
		private readonly route: ActivatedRoute,
		private readonly router: Router,
		private readonly userService: UserService,
		private readonly legalDocumentService: LegalDocumentService,

		private readonly snackBar: MatSnackBar,
		private readonly errorHandler: GlobalErrorHandler
	) {
		this.steps = [
			{
				label: this.translationService.getTR('auction.selection'),
				status: StepStatus.ENABLED,
			},
			{
				label: this.translationService.getTR('auction.bids'),
				status: StepStatus.ENABLED,
			},
			{
				label: this.translationService.getTR('auction.confirmation'),
				status: StepStatus.CURRENT,
			}
		];
		const resolver = <{ auctionOffer: AuctionOffer }> this.route.snapshot.data;
		this.auctionOffer = resolver['auctionOffer'];
		this.defaultSoldTo = <string> this.route.snapshot.params['soldTo'];

		this.user = this.userService.user;
		this.usersoldToSub = this.userService.getSoldTosTransac(AUCTION_SERVICE).subscribe({
			next: res => {
				this.userSoldTos = res;
				this.getCustomerBids(this.auctionOffer.bundles);
			},
			complete: () => {
				this.isLoading = false;
			}
		});
		this.auctionLabelValue = this.translationService.getTR('legal-documents.non.prime.sales.framework');
	}

	ngOnInit() {
		this.auctionIdSub = this.userService.getBocs(
			['name', 'businessId'],
			`businessId:${ this.defaultSoldTo }`
		).subscribe({
			next: soldToList => {
				const soldTos = soldToList.results;

				if (soldTos.length !== 0) {
					this.soldToName = soldTos[0].name;
				}
			}
		});
	}

	onUserAgreementCheck(filterObject:
	{ agreedToSalesConditions: boolean; notAgreedLegalDocumentIds: number[]; isLoading: boolean }) {
		this.isUserAgreementsLoading = filterObject.isLoading;
		this.notAgreedLegalDocumentIds = filterObject.notAgreedLegalDocumentIds;
		this.isConfirmDisabled = Boolean(!filterObject.agreedToSalesConditions);
	}

	async onStepClicked(event: StepClickedEvent) {
		if (event.stepIndex === 0) {
			await this.router.navigate(['../../../../offer-selection'], { relativeTo: this.route });
		}
		if (event.stepIndex === 1) {
			await this.router.navigate(['../'], { relativeTo: this.route });
		}
	}

	getTransportMode(tmode?: string) {
		if (tmode) {
			return staticTransportModeValues.get(tmode)?.name;
		}
		return undefined;
	}

	getCustomerBids(bundleList: Bundle[]) {
		for (const [index, bundle] of bundleList.entries()) {
			const customerBid = bundle.bids.find(bid => bid.soldTo.sapCode === Number(this.defaultSoldTo));
			if (customerBid) {
				this.bidsMap.set(index, customerBid);
			}
		}
	}

	calculateEffectivePrice(exwPrice?: number, transportCost?: number) {
		return ((exwPrice ? exwPrice * 100 : 0) + (Number(transportCost) * 100)) / 100;
	}

	hasCustomerPlacedBidOnBundle(bundle: Bundle) {
		return bundle.bids.some(bid => bid.soldTo.sapCode === Number(this.defaultSoldTo));
	}

	print() {
		print();
	}

	goBack() {
		this.locationService.go(-1);
	}

	ngOnDestroy() {
		this.auctionIdSub?.unsubscribe();
	}

	async confirmBid(event: Event) {
		this.isLoading = true;
		for (const notAgreedLegalDocumentId of this.notAgreedLegalDocumentIds) {
			await this.legalDocumentService.saveUserAgreement(notAgreedLegalDocumentId);
		}
		event.preventDefault();
		try {
			this.submitting = true;
			try {
				await this.auctionOfferService.saveBids();
			} catch (err) {
				console.error(err);
				this.errorHandler.handleError(err);
				this.snackBar.openFromComponent(
					SnackbarInternalNotAuthorizedComponent,
					{
						duration: 5000,
						panelClass: ['copo-claim-draft-snackbar'],
						data: true
					}
				);
				return;
			}
		} finally {
			this.submitting = false;
		}
		await this.router.navigate(['/auction/bids'], { replaceUrl: true });
	}
}
